import React from 'react';
import Box from '@mui/material/Box';
import { Button, ListItemIcon, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import {DataGridPro, GridColumnMenu, GridToolbar} from '@mui/x-data-grid-pro';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ClearIcon from '@mui/icons-material/Clear';
import {dataGridSx} from '../constants/styles';

// ── Affichage du header (alias + nom technique) — sans bouton ────────────
function ColumnHeaderDisplay({ headerName, alias }) {
    return (
        <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
            {alias ? (
                <>
                    <Box sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {alias}
                    </Box>
                    <Box sx={{ fontSize: '0.58rem', opacity: 0.75, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontStyle: 'italic' }}>
                        {headerName}
                    </Box>
                </>
            ) : (
                <Box sx={{ fontSize: '0.70rem', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {headerName}
                </Box>
            )}
        </Box>
    );
}

// ── Items alias injectés dans le menu natif DataGrid ─────────────────────
function AliasColumnMenuItem({ colDef, hideMenu, columnAliases, onAliasChange }) {
    const field = colDef?.field;
    const alias = columnAliases?.[field];
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft]     = React.useState('');
    const inputRef              = React.useRef(null);

    const startEdit = (e) => {
        e.stopPropagation();
        setDraft(alias ?? '');
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const handleConfirm = () => {
        onAliasChange?.(field, draft.trim() || null);
        setEditing(false);
        hideMenu?.();
    };

    if (editing) {
        return (
            <Box sx={{ px: 1.5, py: 1, minWidth: 240 }} onClick={e => e.stopPropagation()}>
                <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.12em', mb: 0.75 }}>
                    Alias de colonne
                </Typography>
                <TextField
                    inputRef={inputRef}
                    autoFocus
                    fullWidth
                    size="small"
                    label="Nom affiché"
                    placeholder="Laisser vide pour réinitialiser"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                        e.stopPropagation();
                        if (e.key === 'Enter') handleConfirm();
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '0.82rem', borderRadius: 1.5 } }}
                />
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setEditing(false)}
                        sx={{ fontSize: '0.72rem', textTransform: 'none', color: 'text.secondary' }}>
                        Annuler
                    </Button>
                    <Button size="small" variant="contained" onClick={handleConfirm}
                        sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                        OK
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <MenuItem onClick={startEdit}>
                <ListItemIcon>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                    {alias ? "Modifier l'alias" : 'Renommer la colonne…'}
                </ListItemText>
            </MenuItem>
            {alias && (
                <MenuItem onClick={() => { onAliasChange?.(field, null); hideMenu?.(); }}>
                    <ListItemIcon>
                        <ClearIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>
                        Effacer l'alias
                    </ListItemText>
                </MenuItem>
            )}
        </>
    );
}

// ── Menu de colonne : étend le menu natif avec les actions alias ──────────
function CustomColumnMenu({ columnAliases, onAliasChange, ...props }) {
    return (
        <GridColumnMenu
            {...props}
            slots={{ columnMenuUserItem: AliasColumnMenuItem }}
            slotProps={{
                columnMenuUserItem: {
                    displayOrder: 50,
                    columnAliases,
                    onAliasChange,
                },
            }}
        />
    );
}

const localeText = {
    toolbarColumns: 'Colonnes',
    toolbarFilters: 'Filtres',
    toolbarDensity: 'Densité',
    toolbarDensityLabel: 'Densité',
    toolbarDensityCompact: 'Compact',
    toolbarDensityStandard: 'Standard',
    toolbarDensityComfortable: 'Confortable',
    columnsPanelTextFieldLabel: 'Rechercher une colonne',
    columnsPanelTextFieldPlaceholder: 'Titre de colonne',
    columnsPanelShowAllButton: 'Tout afficher',
    columnsPanelHideAllButton: 'Tout masquer',
    filterPanelAddFilter: 'Ajouter un filtre',
    filterPanelRemoveAll: 'Tout supprimer',
    filterPanelDeleteIconLabel: 'Supprimer',
    filterPanelLogicOperator: 'Opérateur logique',
    filterPanelOperator: 'Opérateur',
    filterPanelOperatorAnd: 'Et',
    filterPanelOperatorOr: 'Ou',
    filterPanelColumns: 'Colonnes',
    filterPanelInputLabel: 'Valeur',
    filterPanelInputPlaceholder: 'Valeur du filtre',
    filterOperatorContains: 'contient',
    filterOperatorDoesNotContain: 'ne contient pas',
    filterOperatorEquals: 'égal à',
    filterOperatorDoesNotEqual: 'différent de',
    filterOperatorStartsWith: 'commence par',
    filterOperatorEndsWith: 'finit par',
    filterOperatorIsEmpty: 'est vide',
    filterOperatorIsNotEmpty: "n'est pas vide",
    filterOperatorIsAnyOf: 'est parmi',
    noRowsLabel: 'Aucune donnée',
    MuiTablePagination: {
        labelRowsPerPage: 'Lignes par page :',
        labelDisplayedRows: ({from, to, count}) => `${from}–${to} sur ${count}`,
    },
};

/**
 * DataGrid MUI affichant les données agrégées par adresse, avec groupes de
 * colonnes colorés par source et barre d'outils (filtre, export, densité…).
 */
export default function ResultGrid({rows, columns, columnGroupingModel, columnVisibilityModel, onColumnVisibilityModelChange, apiRef, filterModel, onFilterModelChange, columnAliases, onColumnAliasChange, onColumnOrderChange}) {
    const augmentedColumns = React.useMemo(
        () => columns.map(col => ({
            ...col,
            renderHeader: () => (
                <ColumnHeaderDisplay
                    headerName={col.headerName}
                    alias={columnAliases?.[col.field]}
                />
            ),
        })),
        [columns, columnAliases],
    );

    return (
        <Box sx={{mt: 2, mx: 2, height: 'calc(100vh - 130px)'}}>
            <DataGridPro
                apiRef={apiRef}
                rows={rows}
                columns={augmentedColumns}
                columnGroupingModel={columnGroupingModel}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                filterModel={filterModel}
                onFilterModelChange={onFilterModelChange}
                onColumnOrderChange={onColumnOrderChange}
                pagination
                disableMultipleColumnsFiltering={false}
                slots={{ toolbar: GridToolbar, columnMenu: CustomColumnMenu }}
                slotProps={{
                    columnMenu: { columnAliases, onAliasChange: onColumnAliasChange },
                }}
                density="compact"
                getRowHeight={() => 45}
                pageSizeOptions={[15, 25, 50, 100]}
                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                sx={{
                    ...dataGridSx,
                    '& .MuiDataGrid-cell': {
                        alignItems: 'flex-start', py: '4px',
                        overflow: 'hidden',
                    },
                    '& .MuiDataGrid-row': { maxHeight: '45px !important' },
                }}
                localeText={localeText}
            />
        </Box>
    );
}
